import { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { axios, constants, notify, BaseDialog, Loader, PrimaryButton } from '@bento-dental/core';
import { useFormState } from '@bento-dental/core/hooks';
import { formatDateForRequest, isValidDate } from '@bento-dental/core/utilities';
import { useCobra } from './EmployeeUtils';
import CobraElectForm from './CobraElectForm';
import { CobraInfo, CobraElectFormState } from './types';

interface CobraElectDialogProps {
  open: boolean;
  onClose: () => void;
  refresh: () => void;
  patientId?: string | number | null;
}

const initialFormState: CobraElectFormState = {
  employer: null,
  employerError: '',
  planYears: [],
  planYear: null,
  planYearError: '',
  plans: [],
  plan: null,
  planError: '',
  planType: null,
  family: [],
  familyPatientIdsToCover: [],
  familyPatientIdsToCoverError: '',
  effectiveDate: null,
  effectiveDateError: '',
  cobraExpirationPeriodMonths: null,
  cobraExpirationPeriodMonthsError: '',
};

function CobraElectDialog({ open, onClose: onCloseFn, refresh, patientId }: CobraElectDialogProps) {
  const { formState, setFormState } = useFormState(initialFormState) as {
    formState: CobraElectFormState;
    setFormState: (updates: Partial<CobraElectFormState>) => void;
  };
  const { cobraElectionInfo } = useCobra();
  const [showLoader, setShowLoader] = useState(false);
  const [cobraInfo, setCobraInfo] = useState<CobraInfo | null>(null);

  const onClose = () => {
    setFormState(initialFormState);
    onCloseFn();
  };

  const getNewFamilyIdsToCover = () => {
    let familyIdsToCover: string[] = [];

    if (cobraInfo?.family_members) {
      const coveredFamily = cobraInfo.family_members
        .filter((member) => member.has_cobra)
        .map((member) => String(member.patient));

      familyIdsToCover = formState.familyPatientIdsToCover.filter(
        (id) => !coveredFamily.includes(id)
      );
    }
    return familyIdsToCover;
  };

  const canSubmit = (submit: () => void) => {
    const errors: Partial<CobraElectFormState> = {};

    if (!formState.employer) {
      errors.employerError = constants.VALIDATION.REQUIRED;
    }
    if (!formState.planYear) {
      errors.planYearError = constants.VALIDATION.REQUIRED;
    }
    if (!formState.plan) {
      errors.planError = constants.VALIDATION.REQUIRED;
    }
    if (!formState.effectiveDate) {
      errors.effectiveDateError = constants.VALIDATION.REQUIRED;
    } else if (!isValidDate(formState.effectiveDate)) {
      errors.effectiveDateError = constants.VALIDATION.INVALID;
    }
    if (!formState.cobraExpirationPeriodMonths) {
      errors.cobraExpirationPeriodMonthsError = constants.VALIDATION.REQUIRED;
    }
    if (!getNewFamilyIdsToCover().length) {
      errors.familyPatientIdsToCoverError = constants.VALIDATION.REQUIRED;
    }

    if (Object.keys(errors).length > 0) {
      setFormState(errors);
    } else {
      submit();
    }
  };

  const elect = () => {
    if (!patientId) {
      return;
    }
    setShowLoader(true);
    const {
      employer: corp,
      planYear: plan_year,
      plan,
      planType: plan_type,
      effectiveDate: coverage_start_date,
      cobraExpirationPeriodMonths: coverage_period_in_months,
    } = formState;

    const familyIdsToCover = getNewFamilyIdsToCover();

    const body = {
      patient: patientId,
      corp,
      plan_year,
      plan,
      plan_type,
      covered_family_patient_ids: familyIdsToCover,
      coverage_start_date: coverage_start_date
        ? formatDateForRequest(coverage_start_date)
        : coverage_start_date,
      coverage_period_in_months,
    };
    axios
      .post(`corp/cobra/bulk-elect/`, body)
      .then(() => {
        refresh();
        onCloseFn();
        notify('Successfully elected COBRA.', 'success');
        setShowLoader(false);
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : 'Unable to elect COBRA.';
        notify(message, 'error');
        setShowLoader(false);
      });
  };

  useEffect(() => {
    if (open && patientId && cobraElectionInfo) {
      setShowLoader(true);
      cobraElectionInfo
        .mutateAsync({ patientId: String(patientId) })
        .then((response) => {
          setCobraInfo(response.data.data);
        })
        .catch((error) => {
          const message = error instanceof Error ? error.message : 'Unable to load COBRA info.';
          notify(message, 'error');
        })
        .finally(() => {
          setShowLoader(false);
        });
    }
  }, [open, patientId]);

  useEffect(() => {
    if (cobraInfo?.employers?.length) {
      const employer = cobraInfo.employers[0];
      setFormState({ employer: employer.corp ?? null, employerError: '' });
      if (cobraInfo.family_members) {
        setFormState({ family: cobraInfo.family_members });
      }
    }
  }, [cobraInfo, setFormState]);

  useEffect(() => {
    if (!formState.employer || !cobraInfo?.employers?.length) return;
    const employer = cobraInfo.employers[0];
    if (employer.plan_years?.length) {
      setFormState({ planYears: employer.plan_years, planYearError: '' });
      if (employer.plan_years.length === 1) {
        setFormState({ planYear: String(employer.plan_years[0].plan_year) });
      }
    }
  }, [cobraInfo, formState.employer, setFormState]);

  useEffect(() => {
    if (!formState.planYear || !cobraInfo?.employers?.length) return;
    const employer = cobraInfo.employers[0];
    const planYear = employer.plan_years?.find(
      (item) => String(item.plan_year) === String(formState.planYear)
    );
    if (planYear) {
      setFormState({ plans: planYear.plans, planError: '' });
      if (planYear.plans.length === 1) {
        setFormState({
          plan: String(planYear.plans[0].id),
          planType: planYear.plans[0].type,
        });
      }
    }
  }, [cobraInfo, formState.planYear, setFormState]);

  const dialogActions = {
    'Elect COBRA': () => {
      canSubmit(elect);
    },
    Cancel: onClose,
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      dialogName="elect-cobra-dialog"
      fullWidth={false}
      maxWidth="md"
      title="Elect Cobra"
      actionButtonComponent={PrimaryButton}
      actions={dialogActions}
    >
      {showLoader ? (
        <Loader size="sm" />
      ) : (
        <Grid container>
          <CobraElectForm
            patientId={patientId}
            cobraInfo={cobraInfo}
            formState={formState}
            setFormState={setFormState}
          />
        </Grid>
      )}
    </BaseDialog>
  );
}

export default CobraElectDialog;
